import { useContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { User } from '@ridemaadi/types';
import { isPublicMarketingPath } from '../features/public/publicPaths';
import { ApiClientError, apiFetch } from '../lib/api-client';
import { resolveAuthState } from './authSession';
import { AuthContext, type AuthContextValue } from './authContext';

export { StaticAuthProvider } from './authContext';
export type { AuthContextValue } from './authContext';

export function AuthProvider({ children }: { children: ReactNode }) {
  const query = useQuery<User | null>({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      try {
        return await apiFetch<User>('/auth/me');
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 401) return null;
        // Public marketing pages render without backend; treat probe failures as logged out.
        if (isPublicMarketingPath()) return null;
        throw err;
      }
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    retry: false,
  });

  const value: AuthContextValue = resolveAuthState(query);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
